import { useState } from 'react';
import { useNavigate, useRawLocation, Link } from '../routing';
import { authApi } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import PasswordInput from '../components/PasswordInput';
import peakMermaid from '../assets/peak-mermaid.svg';
import peakWordmark from '../assets/peak-wordmark.svg';
import HeartIcon from '../components/HeartIcon';
import * as S from './authStyles';
import './authAnimations.css';
import Icon from '../components/Icon';

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useRawLocation();
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      // Trim both — pasted credentials often carry stray leading/trailing spaces.
      const r = await authApi.login(email.trim(), password.trim());
      login(r.data.token, r.data.user);
      const from = location.state?.from;
      navigate(from || '/', { replace: true, state: from?.state });
    } catch (err) {
      // Distinguish "no network" from "server said no" — different next steps.
      const fallback = err.response
        ? 'משהו השתבש — נסו שוב בעוד רגע'
        : 'אין חיבור לשרת — בדקו את חיבור האינטרנט ונסו שוב';
      setError(err.response?.data?.error || fallback);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ ...S.container, flexDirection: 'column' }}>
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
      <div style={S.card}>
        {/* Logo: mermaid icon (enlarged) stacked above the PEAK TECH wordmark.
            Split from the original single-file lockup so the mermaid can grow
            without scaling the wordmark. */}
        <div style={{ textAlign: 'center', marginBottom: 'clamp(20px, 4vh, 32px)' }}>
          <span className="auth-mermaid-in">
            <img
              src={peakMermaid}
              alt=""
              aria-hidden="true"
              className="auth-mermaid-float"
              style={{ display: 'block', margin: '0 auto', width: 'clamp(72px, 18vw, 94px)', height: 'auto' }}
            />
          </span>
          <img
            src={peakWordmark}
            alt="Peak Tech"
            style={{ display: 'block', margin: '10px auto 0', width: 'clamp(150px, 40vw, 185px)', height: 'auto' }}
          />
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 18 }}>
            <label htmlFor="email" style={S.label}>
              אימייל
            </label>
            <input
              id="email"
              name="email"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              autoComplete="email"
              inputMode="email"
              required
              style={S.input}
              {...S.inputFocusHandlers}
              {...S.validationHandlers}
            />
          </div>

          <div style={{ marginBottom: 24 }}>
            <label htmlFor="password" style={S.label}>
              סיסמה
            </label>
            <PasswordInput
              id="password"
              name="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              style={S.input}
              {...S.inputFocusHandlers}
              {...S.validationHandlers}
            />
          </div>

          {error && (
            <div role="alert" style={S.errorBox}>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            aria-busy={loading}
            style={S.button(loading)}
            {...S.buttonHoverHandlers}
          >
            {loading ? 'נכנסים…' : 'כניסה'}
          </button>

          <div style={{ textAlign: 'center', marginTop: 16 }}>
            <Link to="/forgot-password" style={S.link}>
              שכחתי סיסמה
            </Link>
          </div>
        </form>

        {/* מעבר למערכת האחות — Peak ניהול (קליטת עובדים וניהול שוטף) */}
        <div style={{ textAlign: 'center', marginTop: 22, paddingTop: 18, borderTop: `1px solid ${S.colors.divider}` }}>
          <div style={{ fontSize: '0.8rem', color: S.colors.muted, marginBottom: 8 }}>
            מחפשים את מערכת ניהול העובדים?
          </div>
          <a
            href="https://peak-hr-production.up.railway.app"
            target="_blank"
            rel="noopener noreferrer"
            style={{ ...S.link, fontSize: '0.9rem', fontWeight: 600 }}
          >
            מערכת ניהול עובדים <Icon name="external" size={13} />
          </a>
        </div>
      </div>
      </div>
      <footer
        style={{
          direction: 'ltr',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 5,
          padding: '16px 16px 22px',
          fontSize: '0.8rem',
          color: '#7E8B84',
        }}
      >
        <span>Made with</span>
        <HeartIcon label="love" color="#8AA99B" style={{ position: 'relative', top: '1px' }} />
        <span>by Peak Tech</span>
      </footer>
    </div>
  );
}
